import type React from 'react';
import { useRef, useEffect, useState, memo, useMemo, useCallback } from 'react';
import { ChatMessage } from './ChatMessage';
import { ImageModal } from './ImageModal';
import { UILoaderUnified as Loader } from '@/components/UILoaderUnified';
import { AttachedMedia } from '@/types/aiAgents';
import GeneratingLoader from '@/components/GeneratingLoader';
import { SearchResult } from '@/types/searchResults';
import { AILoadingIndicator } from './AILoadingIndicator';
import { useSmoothScroll } from '@/hooks/useSmoothScroll';
import { useStreamingScroll } from '@/hooks/useStreamingScroll';
import { ChevronUp, Loader2, Eye, EyeOff, Maximize2, Minimize2 } from 'lucide-react';
import chatgptLight from '@/assets/chatgpt_light.png';
import chatgptDark from '@/assets/chatgpt_dark.png';
import deepseekLogo from '@/assets/deepseek.png';
import claudeLogo from '@/assets/claude.png';
import geminiLogo from '@/assets/gemini.png';
import llamaLogo from '@/assets/llama.png';
import qwenLogo from '@/assets/qwen.png';
import ogaiLogo from '@/assets/image.png';
import { storage, AppSettings } from '@/utils/storageManager';
import {
    PerplexityIcon,
    KimiIcon,
    GoogleAIIcon,
    ClaudeIcon,
    GPTIcon,
    GeminiIcon,
    LlamaIcon,
    DeepseekIcon,
    QwenIcon
} from "./icons";
import { GlassToggle } from "@/components/ui/GlassToggle";

interface Message {
    id: number;
    content: string;
    type: 'user' | 'ai';
    timestamp: Date;
    model: string;
    isImage?: boolean;
    attachedMedia?: AttachedMedia;
    selectedAgent?: any;
    agentData?: any;
    searchResults?: SearchResult;
    branchedFrom?: {
        chatId: string | null;
        messageId: number;
        summary?: string;
    };
    // Multi-AI grouping id; messages with same id are shown side-by-side
    multiBatchId?: string;
}

interface ChatInterfaceProps {
    messages: any[];
    isLoading: boolean;
    isAttachmentUploading?: boolean;
    isDarkMode: boolean;
    isImageMode: boolean;
    isWebSearchMode: boolean;
    isDeepResearchMode: boolean;

    selectedModel: string;
    selectedAgent?: any;
    loadingError?: string;
    onRegenerate: (messageId?: number) => void;
    onShare: () => void;
    onEdit: (content: string) => void;
    streamingMessageId?: number;
    onBranch?: (messageId: number) => void;
    onAskOtherAI?: (aiMessageId: number, selectedModel: string) => void;
    loadingContext?: { context: 'image' | 'chat-agent' | 'chat-model' | 'web-search' | 'deep-research' | 'quiz'; name: string; userQuery?: string } | null;
    hasMoreMessages?: boolean;
    isLoadingMore?: boolean;
    onLoadMore?: () => void;
}

export const ChatInterface = memo(({
    messages,
    isLoading,
    isAttachmentUploading = false,
    isDarkMode,
    isImageMode,
    isWebSearchMode,
    isDeepResearchMode,
    selectedModel,
    selectedAgent,
    loadingError,
    onRegenerate,
    onShare,
    onEdit,
    streamingMessageId,
    onBranch,
    onAskOtherAI,
    loadingContext,
    hasMoreMessages = false,
    isLoadingMore = false,
    onLoadMore,
}: ChatInterfaceProps) => {
    const [imageModal, setImageModal] = useState<{ images: string[]; index: number } | null>(null);
    // Refs for positioning
    const lastMessageRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);
    // Hidden state for individual multi-AI responses (by message id)
    const [hiddenResponses, setHiddenResponses] = useState<Set<number>>(() => new Set());
    const toggleHidden = useCallback((id: number) => {
        setHiddenResponses(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);
    // Expanded state for individual multi-AI responses (by message id)
    const [expandedResponses, setExpandedResponses] = useState<Set<number>>(() => new Set());
    const toggleExpanded = useCallback((id: number) => {
        setExpandedResponses(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);
    // Mirror app background mode to style multi-AI body
    const [backgroundType, setBackgroundType] = useState<'solid' | 'image'>(() => {
        try {
            const settings = storage.getAppSettings() as AppSettings;
            const bt = (settings?.backgroundType as 'solid' | 'image' | undefined) ?? 'solid';
            return bt === 'image' ? 'image' : 'solid';
        } catch {
            return 'solid';
        }
    });

    useEffect(() => {
        const handleAppSettingsChange = (e: CustomEvent) => {
            const nextType = (e as any).detail?.backgroundType;
            if (nextType === 'solid' || nextType === 'image') {
                setBackgroundType(nextType);
            } else {
                setBackgroundType('solid');
            }
        };

        window.addEventListener('appSettingsChanged', handleAppSettingsChange as EventListener);
        return () => {
            window.removeEventListener('appSettingsChanged', handleAppSettingsChange as EventListener);
        };
    }, []);

    // Track a temporary "chat with only this AI next" flag
    const [chatWithOnlyNext, setChatWithOnlyNext] = useState<string | null>(() => {
        try { return localStorage.getItem('chatWithOnlyNext'); } catch { return null; }
    });

    useEffect(() => {
        const handler = () => {
            try { setChatWithOnlyNext(localStorage.getItem('chatWithOnlyNext')); } catch { }
        };
        const onCustom = () => handler();
        window.addEventListener('storage', handler);
        window.addEventListener('chatWithOnlyChanged', onCustom as EventListener);
        return () => {
            window.removeEventListener('storage', handler);
            window.removeEventListener('chatWithOnlyChanged', onCustom as EventListener);
        };
    }, []);

    const setChatWithOnly = useCallback((serviceModel: string | null) => {
        try {
            if (serviceModel) localStorage.setItem('chatWithOnlyNext', serviceModel);
            else localStorage.removeItem('chatWithOnlyNext');
            setChatWithOnlyNext(serviceModel);
            try { window.dispatchEvent(new Event('chatWithOnlyChanged')); } catch { }
        } catch { }
    }, []);

    // Map display label to service id and logo used in headers
    const mapUIToService = useCallback((m: string): string => {
        const key = (m || '').toLowerCase();
        if (key === 'chat gpt' || key === 'chatgpt' || key === 'gpt') return 'chatgpt';
        if (key === 'solar pro') return 'upstage';
        if (key === 'gpt 5') return 'gpt5';
        return key;
    }, []);

    const getLogoFor = useCallback((displayName: string): string | undefined => {
        const key = (displayName || '').toLowerCase();
        switch (key) {
            case 'chat gpt':
                return isDarkMode ? chatgptLight : chatgptDark;
            case 'gpt':
                return isDarkMode ? chatgptLight : chatgptDark;
            case 'gpt 5':
            case 'gpt-5':
            case 'gpt5':
                return isDarkMode ? chatgptLight : chatgptDark;
            case 'chatgpt':
                return isDarkMode ? chatgptLight : chatgptDark;
            case 'glm':
                return isDarkMode ? chatgptLight : chatgptDark;
            case 'deepseek':
                return deepseekLogo;
            case 'claude':
                return claudeLogo;
            case 'gemini':
                return geminiLogo;
            case 'llama':
                return llamaLogo;
            case 'qwen':
                return qwenLogo;
            case 'sambanova':
            case 'cerebras':
            case 'upstage':
                return ogaiLogo;
            case 'solar pro':
                return ogaiLogo;
            default:
                return ogaiLogo;
        }
    }, [isDarkMode]);

    // Use smooth scrolling hook for regular messages
    const { messagesEndRef: regularMessagesEndRef, setScrollContainer: setRegularScrollContainer, scrollToBottom } = useSmoothScroll({
        enabled: true,
        scrollThreshold: 150,
        scrollDuration: 200,
    });

    // Use dedicated streaming scroll hook for streaming messages
    const {
        messagesEndRef: streamingMessagesEndRef,
        setScrollContainer: setStreamingScrollContainer,
        startStreamingScroll,
        stopStreamingScroll
    } = useStreamingScroll({
        enabled: true,
        scrollThreshold: 150,
        updateInterval: 8, // ~120fps
    });

    // Use streaming scroll container for both
    const setScrollContainer = useCallback((element: HTMLElement | null) => {
        scrollContainerRef.current = element;
        setRegularScrollContainer(element);
        setStreamingScrollContainer(element);
        try {
            window.dispatchEvent(new CustomEvent('chatScrollContainerChanged', { detail: { element } }));
        } catch {
        }
    }, [setRegularScrollContainer, setStreamingScrollContainer]);

    // Use streaming messages end ref
    const messagesEndRef = streamingMessagesEndRef;

    // Handle scrolling during streaming only; do not force any scroll when AI finishes
    useEffect(() => {
        if (isLoading && streamingMessageId) {
            startStreamingScroll();
        } else {
            stopStreamingScroll();
        }
    }, [isLoading, streamingMessageId, startStreamingScroll, stopStreamingScroll]);

    // Disable generic bottom scroll to avoid double movements. Streaming hook handles AI.
    useEffect(() => {
        // intentionally no-op
    }, [messages.length]);

    // Track user messages and scroll navigation active dot
    const userMessages = useMemo(() => {
        return messages.filter(m => m.type === 'user');
    }, [messages]);

    const [activeMessageId, setActiveMessageId] = useState<number | null>(null);
    const [hoveredMsg, setHoveredMsg] = useState<{ id: number; rect: DOMRect; content: string } | null>(null);

    useEffect(() => {
        if (userMessages.length === 0) return;

        const findActive = () => {
            let activeId: number | null = null;
            let minDist = Infinity;
            // prefer the last message whose top is above the 1/3 viewport mark
            const anchor = window.innerHeight * 0.35;

            for (const msg of userMessages) {
                const el = document.getElementById(`chat-msg-${msg.id}`);
                if (el) {
                    const top = el.getBoundingClientRect().top;
                    const dist = Math.abs(top - anchor);
                    if (dist < minDist) {
                        minDist = dist;
                        activeId = msg.id as number;
                    }
                }
            }
            if (activeId !== null) setActiveMessageId(activeId);
        };

        // Listen on the scroll container (captured)
        window.addEventListener('scroll', findActive, true);
        window.addEventListener('resize', findActive, { passive: true });
        findActive();

        return () => {
            window.removeEventListener('scroll', findActive, true);
            window.removeEventListener('resize', findActive);
        };
    }, [userMessages]);

    const scrollToMessage = useCallback((msgId: number) => {
        const el = document.getElementById(`chat-msg-${msgId}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveMessageId(msgId);
        }
    }, []);

    // Align the last USER message to just below the header using CSS scroll-margin
    useEffect(() => {
        if (messages.length === 0) return;
        const last = messages[messages.length - 1] as any;
        const lastIsUser = last?.type === 'user' || last?.role === 'user';
        if (!lastIsUser) return; // only move for user messages
        const id = window.setTimeout(() => {
            lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        return () => window.clearTimeout(id);
    }, [messages.length]);

    useEffect(() => {
        const handleImageModal = (event: any) => {
            const { images, index } = event.detail;
            setImageModal({ images, index });
        };

        const handleWikipediaImageClick = (event: any) => {
            const { images, index } = event.detail;
            setImageModal({ images, index });
        };

        window.addEventListener('openImageModal', handleImageModal);
        window.addEventListener('wikipediaImageClick', handleWikipediaImageClick);

        return () => {
            window.removeEventListener('openImageModal', handleImageModal);
            window.removeEventListener('wikipediaImageClick', handleWikipediaImageClick);
        };
    }, []);

    const getDisplayModelName = useCallback(() => {
        if (selectedAgent) {
            return selectedAgent.name;
        }
        return selectedModel.charAt(0).toUpperCase() + selectedModel.slice(1);
    }, [selectedAgent, selectedModel]);

    const handleRegenerate = useCallback((messageId?: number) => {
        onRegenerate(messageId);
    }, [onRegenerate]);

    const memoizedMessages = useMemo(() => {
        return messages; // Render all messages
    }, [messages]);

    // Intersection observer for load more button
    useEffect(() => {
        if (!hasMoreMessages || !loadMoreRef.current || !onLoadMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !isLoadingMore) {
                        onLoadMore();
                    }
                });
            },
            {
                root: scrollContainerRef.current,
                rootMargin: '100px',
                threshold: 0.1,
            }
        );

        observer.observe(loadMoreRef.current);

        return () => {
            observer.disconnect();
        };
    }, [hasMoreMessages, isLoadingMore, onLoadMore]);

    // Show/hide load more button based on scroll position
    useEffect(() => {
        setShowLoadMoreButton(hasMoreMessages && messages.length > 10);
    }, [hasMoreMessages, messages.length]);

    // Auto-show/hide controls strip (4s show, then hide; re-show on hover/tap)
    const [controlsVisible, setControlsVisible] = useState(true);
    const [controlsHovering, setControlsHovering] = useState(false);
    const [controlsInteracting, setControlsInteracting] = useState(false);
    const hideTimerRef = useRef<number | null>(null);

    const scheduleHide = useCallback(() => {
        if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = window.setTimeout(() => {
            if (!controlsHovering && !controlsInteracting) setControlsVisible(false);
        }, 4000);
    }, [controlsHovering, controlsInteracting]);

    useEffect(() => {
        // Initial 4s show on mount
        scheduleHide();
        return () => {
            if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
        };
    }, [scheduleHide]);

    const onHDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (e.button !== 0) return; // left click only
        if (e.target !== e.currentTarget) return; // start only when clicking empty container area
        const el = e.currentTarget as HTMLDivElement & { _dragging?: boolean; _startX?: number; _scrollLeft?: number; _prevUserSelect?: string };
        try {
            el.setPointerCapture?.(e.pointerId);
        } catch {
            // Ignore setPointerCapture errors if pointer is not active
        }
        el._dragging = true;
        el._startX = e.clientX;
        el._scrollLeft = el.scrollLeft;
        el._prevUserSelect = document.body.style.userSelect;
        document.body.style.userSelect = 'none';
    }, []);

    const onHDragMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const el = e.currentTarget as HTMLDivElement & { _dragging?: boolean; _startX?: number; _scrollLeft?: number };
        if (!el._dragging) return;
        const dx = e.clientX - (el._startX || 0);
        el.scrollLeft = (el._scrollLeft || 0) - dx;
        e.preventDefault();
    }, []);

    const onHDragEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const el = e.currentTarget as HTMLDivElement & { _dragging?: boolean; _prevUserSelect?: string };
        try {
            el.releasePointerCapture?.(e.pointerId);
        } catch {
            // Ignore releasePointerCapture errors if pointer is not captured
        }
        el._dragging = false;
        document.body.style.userSelect = el._prevUserSelect || '';
        el.style.cursor = '';
    }, []);

    const [activeChatImageAspectClass, setActiveChatImageAspectClass] = useState<string | null>(null);

    const getChatImageAspectClass = useCallback(() => {
        let ratio: string = '1:1';
        try {
            const stored = localStorage.getItem('chatImageAspectRatio');
            if (stored) ratio = stored;
        } catch {
            // ignore localStorage errors and fall back to default
        }

        switch (ratio) {
            case '1:1':
                return 'aspect-square';
            case '16:9':
                return 'aspect-video';
            case '9:16':
                return 'aspect-[9/16]';
            case '4:3':
                return 'aspect-[4/3]';
            case '3:2':
                return 'aspect-[3/2]';
            case '21:9':
                return 'aspect-[21/9]';
            case '2:3':
                return 'aspect-[2/3]';
            case '3:4':
                return 'aspect-[3/4]';
            default:
                return 'aspect-[3/4]';
        }
    }, []);

    useEffect(() => {
        if (isLoading && isImageMode) {
            // Snapshot the current aspect at the start of an image generation
            setActiveChatImageAspectClass(prev => prev || getChatImageAspectClass());
        } else {
            // Reset when loading finishes or mode changes
            if (!isLoading || !isImageMode) {
                setActiveChatImageAspectClass(null);
            }
        }
    }, [isLoading, isImageMode, getChatImageAspectClass]);

    return (
        <div className='h-full overflow-x-hidden'>
            <div
                ref={setScrollContainer}
                data-chat-scroll-container
                className="flex-1 overflow-y-auto overflow-x-hidden relative z-10 pt-2 h-full"
            >
                <div className="max-w-4xl mx-auto space-y-0 pb-4" data-messages-container>
                    {/* Load More Messages Section */}
                    {hasMoreMessages && (
                        <div ref={loadMoreRef} className="flex justify-center py-4">
                            {isLoadingMore ? (
                                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${isDarkMode ? 'bg-[#292929] text-gray-300' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm"></span>
                                </div>
                            ) : (
                                <button
                                    onClick={onLoadMore}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${isDarkMode
                                        ? 'bg-[#303030] hover:bg-[#2c2c2c] text-gray-200 border border-[#2c2c2c]'
                                        : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm'
                                        }`}
                                >
                                    <ChevronUp className="w-4 h-4" />
                                    <span className="text-sm font-medium">Show older messages</span>
                                </button>
                            )}
                        </div>
                    )}
                    {(() => {
                        const rows: JSX.Element[] = [];
                        const consumed = new Set<number>();
                        // Track which batchIds we have already rendered to avoid duplicate groups
                        const renderedBatch = new Set<string>();
                        for (let i = 0; i < memoizedMessages.length; i++) {
                            const message = memoizedMessages[i] as any;
                            if (consumed.has(message.id)) continue;
                            const isLast = i === memoizedMessages.length - 1;
                            const batchId = message?.multiBatchId as string | undefined;
                            if (batchId && !renderedBatch.has(batchId)) {
                                // Collect all messages in this batch from the entire list to persist grouping across reloads
                                const group = memoizedMessages
                                    .filter((m: any) => m?.multiBatchId === batchId)
                                    .slice()
                                    .sort((a: any, b: any) => {
                                        const ao = a?.multiOrder ?? 9999;
                                        const bo = b?.multiOrder ?? 9999;
                                        if (ao !== bo) return ao - bo;
                                        const at = a?.timestamp ? new Date(a.timestamp as any).getTime() : 0;
                                        const bt = b?.timestamp ? new Date(b.timestamp as any).getTime() : 0;
                                        if (at !== bt) return at - bt;
                                        const aid = String(a?.id ?? '');
                                        const bid = String(b?.id ?? '');
                                        return aid.localeCompare(bid);
                                    });
                                group.forEach((m: any) => consumed.add(m.id));
                                renderedBatch.add(batchId);
                                rows.push(
                                    <div
                                        key={`batch-${batchId}`}
                                        className={`w-full overflow-x-auto [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full
                        ${isDarkMode
                                                ? '[&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-600/50 hover:[&::-webkit-scrollbar-thumb]:bg-gray-500/50'
                                                : '[&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-400/50 hover:[&::-webkit-scrollbar-thumb]:bg-gray-500/50'}`}
                                        style={{ touchAction: 'pan-x pan-y' }}
                                        onPointerDown={onHDragStart}
                                        onPointerMove={onHDragMove}
                                        onPointerUp={onHDragEnd}
                                        onPointerCancel={onHDragEnd}
                                        onPointerLeave={onHDragEnd}
                                    >
                                        <div
                                            className={`flex gap-1 snap-x snap-mandatory px-[1px] sm:px-2 py-2 mb-4 mt-1`}
                                        >
                                            {group.map((m: any) => {
                                                const display = m.displayModel || m.model;
                                                const service = mapUIToService(display);
                                                const activeOnly = chatWithOnlyNext === service;
                                                const logo = getLogoFor(display);
                                                const collapsed = hiddenResponses.has(m.id);
                                                const expanded = expandedResponses.has(m.id);
                                                return (
                                                    <div
                                                        key={m.id}
                                                        className={`${collapsed ? 'min-w-[100px] max-w-[100px]' : expanded ? 'sm:min-w-[100%] sm:max-w-[100%] min-w-[100%]' : 'min-w-[100%] sm:min-w-[420px]'} snap-start transition-all duration-200`}
                                                        style={{ transitionProperty: 'min-width, max-width, width' }}
                                                    >
                                                        <div className={`${isDarkMode ? 'border-[#202020]' : 'bg-white border-gray-200'} border rounded-2xl overflow-hidden relative`}>
                                                            {/* Header with model logo/name and one-shot toggle */}
                                                            <div
                                                                className={`flex items-center justify-between px-3 py-2 overflow-visible select-none ${collapsed ? (isDarkMode ? 'text-gray-200 bg-transparent' : 'text-gray-800 bg-transparent') : (isDarkMode ? 'text-gray-200 bg-[#20202095]' : 'text-gray-800 bg-white')} cursor-grab active:cursor-grabbing`}
                                                                onPointerDown={(e) => {
                                                                    // Only start drag if clicking on the header itself, not buttons inside
                                                                    if (e.target === e.currentTarget) {
                                                                        const container = e.currentTarget.closest('[style*="touch-action"]') as HTMLElement;
                                                                        if (container) {
                                                                            container.dispatchEvent(new PointerEvent('pointerdown', {
                                                                                clientX: e.clientX,
                                                                                clientY: e.clientY,
                                                                                button: 0,
                                                                                bubbles: true
                                                                            }));
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <div className="flex items-center gap-2 text-sm font-medium">
                                                                    {(() => {
                                                                        const name = (display || '').toString().toLowerCase();
                                                                        if (collapsed) return null;
                                                                        if (name.includes('perplexity')) {
                                                                            return <PerplexityIcon className="w-5 h-5" />;
                                                                        }
                                                                        if (name.includes('claude')) {
                                                                            return <ClaudeIcon className="w-5 h-5 text-[#D97757]" />;
                                                                        }
                                                                        if (name.includes('gpt')) {
                                                                            return <GPTIcon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} />;
                                                                        }
                                                                        if (name.includes('gemini')) {
                                                                            return <GeminiIcon className="w-5 h-5" />;
                                                                        }
                                                                        if (name.includes('llama')) {
                                                                            return <LlamaIcon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} />;
                                                                        }
                                                                        if (name.includes('deepseek')) {
                                                                            return <DeepseekIcon className="w-5 h-5" />;
                                                                        }
                                                                        if (name.includes('qwen')) {
                                                                            return <QwenIcon className="w-5 h-5" />;
                                                                        }
                                                                        if (name.includes('google ai')) {
                                                                            return <GoogleAIIcon className="w-5 h-5" />;
                                                                        }
                                                                        if (name === 'kimi') {
                                                                            return <KimiIcon className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} />;
                                                                        }
                                                                        return logo ? (
                                                                            <img src={logo} alt={display} className={`w-5 h-5 rounded-full object-cover`} />
                                                                        ) : null;
                                                                    })()}
                                                                    {!collapsed && (
                                                                        <span className="text-xs text-muted-foreground font-normal">{(display || '').toString()}</span>
                                                                    )}
                                                                </div>
                                                                {!collapsed && (
                                                                    <div className="flex items-center gap-2 relative z-20">
                                                                        {/* Expand/Collapse - only on >= sm */}
                                                                        <div className="relative group z-20">
                                                                            <button
                                                                                onClick={() => toggleExpanded(m.id)}
                                                                                className={`hidden sm:inline-flex relative h-6 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors focus:outline-none ${isDarkMode ? 'bg-[#2a2a2a] hover:bg-[#3a3a3a]' : 'bg-gray-300 hover:bg-gray-400'}`}
                                                                                aria-pressed={expanded}
                                                                            >
                                                                                {expanded ? (
                                                                                    <Minimize2 className="w-4 h-4 text-white" />
                                                                                ) : (
                                                                                    <Maximize2 className="w-4 h-4 text-white" />
                                                                                )}
                                                                            </button>
                                                                            <div className="hidden sm:block absolute left-1/2 -translate-x-1/2 top-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[9999999]">
                                                                                {expanded ? 'Collapse' : 'Expand'}
                                                                            </div>
                                                                        </div>

                                                                        {/* Hide/Show */}
                                                                        <div className="relative group">
                                                                            <button
                                                                                onClick={() => toggleHidden(m.id)}
                                                                                className={`relative inline-flex h-6 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors focus:outline-none ${isDarkMode ? 'bg-[#2a2a2a] hover:bg-[#3a3a3a]' : 'bg-gray-300 hover:bg-gray-400'}`}
                                                                                aria-pressed={hiddenResponses.has(m.id)}
                                                                            >
                                                                                {hiddenResponses.has(m.id) ? (
                                                                                    <Eye className="w-4 h-4 text-white" />
                                                                                ) : (
                                                                                    <EyeOff className="w-4 h-4 text-white" />
                                                                                )}
                                                                            </button>
                                                                            <div className="hidden sm:block absolute left-1/2 -translate-x-1/2 top-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[9999999]">
                                                                                {hiddenResponses.has(m.id) ? 'Show' : 'Hide'}
                                                                            </div>
                                                                        </div>

                                                                        {/* One-shot toggle */}
                                                                        <div className="relative group">
                                                                            <GlassToggle
                                                                                isActive={activeOnly}
                                                                                onToggle={(v) => setChatWithOnly(v ? service : null)}
                                                                                isDarkMode={isDarkMode}
                                                                                label={`Chat with ${display} only`}
                                                                            />
                                                                            <div className="hidden sm:block absolute -right-1 top-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[9999999]">
                                                                                {`Chat with ${display} only`}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {activeOnly && !collapsed && (
                                                                <div className={`px-3 pb-1 ${backgroundType === 'image' ? (isDarkMode ? 'bg-[#1c1c1c]' : 'bg-transparent') : ''}`}>
                                                                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-600/10 text-emerald-700'}`}>Chat with {display} only</span>
                                                                </div>
                                                            )}

                                                            {/* Message body */}
                                                            <div className={`px-2 pb-2 ${collapsed ? 'pt-3' : ''} ${backgroundType === 'image' ? (isDarkMode ? 'bg-[#1c1c1c]' : 'bg-transparent') : ''}`}>
                                                                {hiddenResponses.has(m.id) ? (
                                                                    <div className={`flex flex-col items-center justify-center min-h-[140px] py-2 ${isDarkMode ? '' : ''}`}>
                                                                        {(() => {
                                                                            if (!display) return null;
                                                                            const name = (display || '').toString().toLowerCase();
                                                                            if (name.includes('perplexity')) {
                                                                                return <PerplexityIcon className="w-10 h-10 mb-3" />;
                                                                            }
                                                                            if (name.includes('claude')) {
                                                                                return <ClaudeIcon className="w-10 h-10 text-[#D97757] mb-3" />;
                                                                            }
                                                                            if (name.includes('gpt')) {
                                                                                return <GPTIcon className={`w-10 h-10 mb-3 ${isDarkMode ? 'text-white' : 'text-black'}`} />;
                                                                            }
                                                                            if (name.includes('gemini')) {
                                                                                return <GeminiIcon className="w-10 h-10 mb-3" />;
                                                                            }
                                                                            if (name.includes('llama')) {
                                                                                return <LlamaIcon className={`w-10 h-10 mb-3 ${isDarkMode ? 'text-white' : 'text-black'}`} />;
                                                                            }
                                                                            if (name.includes('deepseek')) {
                                                                                return <DeepseekIcon className="w-10 h-10 mb-3" />;
                                                                            }
                                                                            if (name.includes('qwen')) {
                                                                                return <QwenIcon className="w-10 h-10 mb-3" />;
                                                                            }
                                                                            if (name.includes('google ai')) {
                                                                                return <GoogleAIIcon className="w-10 h-10 mb-3" />;
                                                                            }
                                                                            if (name === 'kimi') {
                                                                                return <KimiIcon className={`w-10 h-10 mb-3 ${isDarkMode ? 'text-white' : 'text-black'}`} />;
                                                                            }
                                                                            return <img src={ogaiLogo} alt={display?.toString() || 'AI'} className="w-10 h-10 rounded-full object-cover mb-3" />;
                                                                        })()}
                                                                        <button
                                                                            onClick={() => toggleHidden(m.id)}
                                                                            aria-label={`Show response`}
                                                                            className={`inline-flex items-center justify-center h-10 w-10 rounded-2xl border transition-colors ${isDarkMode ? 'bg-[#1a1a1a] border-[#2e2e2e] text-gray-200 hover:bg-[#232323]' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                                                                        >
                                                                            <Maximize2 className="w-5 h-5" />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <ChatMessage
                                                                        message={m}
                                                                        isStreaming={(isLoading && m.id === streamingMessageId) || !!(m as any).isStreaming}
                                                                        isDarkMode={isDarkMode}
                                                                        isWebSearchMode={isWebSearchMode}
                                                                        isImageMode={isImageMode}
                                                                        onRegenerate={() => handleRegenerate(m.id)}
                                                                        onShare={onShare}
                                                                        onEdit={onEdit}
                                                                        onBranch={onBranch}
                                                                        onAskOtherAI={onAskOtherAI}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                                continue;
                            }
                            // Regular single message render
                            rows.push(
                                <div
                                    key={message.id}
                                    id={`chat-msg-${message.id}`}
                                    ref={isLast ? lastMessageRef : null}
                                    data-message-wrapper
                                    className={isLast ? '' : undefined}
                                    style={{ scrollMarginTop: '5.5rem' }}
                                >
                                    <ChatMessage
                                        message={message}
                                        isStreaming={(isLoading && message.id === streamingMessageId) || !!(message as any).isStreaming}
                                        isDarkMode={isDarkMode}
                                        isWebSearchMode={isWebSearchMode}
                                        isImageMode={isImageMode}
                                        onRegenerate={() => handleRegenerate(message.id)}
                                        onShare={onShare}
                                        onEdit={onEdit}
                                        onBranch={onBranch}
                                        onAskOtherAI={onAskOtherAI}
                                    />
                                </div>
                            );
                        }
                        return rows;
                    })()}
                    {(isLoading || isAttachmentUploading) && (
                        <div className="mt-2">
                            {isAttachmentUploading && !isLoading ? (
                                <div className="flex justify-start">
                                    <div className="bg-transparent rounded-lg p-4 flex items-center space-x-3">
                                        <Loader isDarkMode={isDarkMode} context={'chat-model'} name={'Uploading'} userQuery={undefined} />
                                    </div>
                                </div>
                            ) : isImageMode ? (
                                <div className="flex justify-start pl-0">
                                    <div className={`${activeChatImageAspectClass || getChatImageAspectClass()} h-[250px] w-[250px] rounded-xl flex flex-col items-center justify-center border overflow-hidden ${isDarkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-gray-200"}`}>
                                        <GeneratingLoader isDarkMode={isDarkMode} />
                                    </div>
                                </div>
                            ) : isWebSearchMode ? (
                                <div className="flex justify-start">
                                    <div className="bg-transparent rounded-lg p-4 flex items-center space-x-3">
                                        <Loader isDarkMode={isDarkMode} context={'web-search'} name={'Search'} userQuery={loadingContext?.userQuery || localStorage.getItem('currentQuery') || undefined} />
                                    </div>
                                </div>
                            ) : isDeepResearchMode ? (
                                <div className="flex justify-start">
                                    <div className="bg-transparent rounded-lg p-4 flex items-center space-x-3">
                                        <Loader isDarkMode={isDarkMode} context={'deep-research'} name={'Research'} userQuery={loadingContext?.userQuery || localStorage.getItem('currentQuery') || undefined} />
                                    </div>
                                </div>
                            ) : (
                                <AILoadingIndicator
                                    modelName={getDisplayModelName()}
                                    isLoading={isLoading}
                                    isError={!!loadingError}
                                    errorMessage={loadingError}
                                    isDarkMode={isDarkMode}
                                    contextOverride={loadingContext?.context || (selectedAgent ? 'chat-agent' : 'chat-model')}
                                    nameOverride={loadingContext?.name || getDisplayModelName()}
                                    userQuery={loadingContext?.userQuery || localStorage.getItem('currentQuery') || undefined}
                                />
                            )}
                        </div>
                    )}
                    <div
                        aria-hidden
                        className="pointer-events-none select-none h-[40vh] md:h-[55vh]"
                    />

                    <div ref={messagesEndRef} data-messages-end />
                </div>
            </div>

            {imageModal && (
                <ImageModal
                    images={imageModal.images}
                    initialIndex={imageModal.index}
                    onClose={() => setImageModal(null)}
                    isDarkMode={isDarkMode}
                />
            )}

            {/* Floating Dots Navigation Bar */}
            {messages.length > 2 && userMessages.length > 0 && (
                <div 
                    className="fixed -right-0.5 top-1/2 -translate-y-1/2 z-50 flex flex-col items-center gap-2 py-4 w-7 transition-all duration-300 max-h-[60vh] overflow-y-auto pointer-events-auto select-none no-scrollbar"
                    style={{
                        scrollbarWidth: 'none',
                        backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    }}
                >
                    <style>{`
                        .no-scrollbar::-webkit-scrollbar {
                            display: none;
                        }
                        /* Remove default button focus ring ripple */
                        .dot-nav-btn:focus { outline: none; }
                        .dot-nav-btn:active { background: transparent; }
                        .dot-nav-btn::-moz-focus-inner { border: 0; }
                    `}</style>
                    <div className="flex flex-col items-center gap-1 no-scrollbar w-full">
                        {userMessages.map((msg, idx) => {
                            const isActive = activeMessageId === msg.id;
                            const activeIndex = activeMessageId !== null 
                                ? userMessages.findIndex(m => m.id === activeMessageId)
                                : 0;
                            const distance = activeIndex === -1 ? 0 : Math.abs(idx - activeIndex);

                            // Calculate scale and opacity based on distance from active dot
                            let dotScale = 1.0;
                            let dotOpacity = 1.0;
                            if (distance === 0) {
                                dotScale = 1.0;
                                dotOpacity = 1.0;
                            } else if (distance === 1) {
                                dotScale = 0.75;
                                dotOpacity = 0.55;
                            } else if (distance === 2) {
                                dotScale = 0.5;
                                dotOpacity = 0.22;
                            } else {
                                dotScale = 0;
                                dotOpacity = 0;
                            }

                            if (dotScale === 0) return null;

                            // Single-line truncation at ~30 chars
                            const cleanText = (msg.content || '')
                                .replace(/<[^>]*>/g, '')
                                .replace(/[#*`_]/g, '')
                                .replace(/\s+/g, ' ')
                                .trim();
                            const truncatedContent = cleanText.length > 30 
                                ? cleanText.slice(0, 30) + '…' 
                                : cleanText;

                            return (
                                <button
                                    key={msg.id}
                                    onClick={() => scrollToMessage(msg.id)}
                                    onMouseEnter={(e) => {
                                        if (window.innerWidth < 1024) return;
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setHoveredMsg({ id: msg.id, rect, content: truncatedContent });
                                    }}
                                    onMouseLeave={() => setHoveredMsg(null)}
                                    className="dot-nav-btn relative w-6 h-6 flex items-center justify-center focus:outline-none"
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                                >
                                    <span 
                                        className={`rounded-full ${
                                            isActive 
                                                ? (isDarkMode ? 'bg-white' : 'bg-neutral-800') 
                                                : (isDarkMode ? 'bg-neutral-500' : 'bg-neutral-400')
                                        }`} 
                                        style={{
                                            width: '5px',
                                            height: '5px',
                                            transform: `scale(${dotScale})`,
                                            opacity: dotOpacity,
                                            transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease, background-color 0.15s',
                                            display: 'block',
                                        }}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Viewport-level tooltip — rendered at the viewport level so it's never clipped */}
            {hoveredMsg && (
                <div 
                    className="fixed z-[9999] select-none pointer-events-none"
                    style={{
                        top: `${hoveredMsg.rect.top + hoveredMsg.rect.height / 2}px`,
                        // Place it just to the left of the dot, with a small gap
                        right: `${window.innerWidth - hoveredMsg.rect.left + 8}px`,
                        transform: 'translateY(-50%)',
                    }}
                >
                    <div
                        style={{
                            background: isDarkMode ? '#2a2a2a' : '#e8e8e8',
                            color: isDarkMode ? '#f0f0f0' : '#171717',
                            padding: '5px 10px',
                            borderRadius: '7px',
                            fontSize: '12px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                            position: 'relative',
                        }}
                    >
                        {hoveredMsg.content}
                        {/* Arrow pointing right */}
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '100%',
                            transform: 'translateY(-50%)',
                            width: 0,
                            height: 0,
                            borderTop: '5px solid transparent',
                            borderBottom: '5px solid transparent',
                            borderLeft: `5px solid ${isDarkMode ? '#2a2a2a' : '#e8e8e8'}`,
                        }} />
                    </div>
                </div>
            )}
        </div>
    );
});

ChatInterface.displayName = 'ChatInterface';
